<script setup>
/* global axios */
/* eslint-disable prettier/prettier */
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { previewSegments, bulletSelection } from './messageFormatting.mjs';

const { t } = useI18n();
const { accountId } = useAccount();
const inboxes = ref([]);
const enabledInboxIds = ref([]);
const isSchedulerEnabled = ref(false);
const schedules = ref([]);
const contacts = ref([]);
const contactSearch = ref('');
const selectedContacts = ref([]);
const groupJidsText = ref('');
const defaultTimezone = ref('America/Sao_Paulo');
let contactSearchTimer;
const isLoading = ref(false);
const isSaving = ref(false);
const messageTextarea = ref(null);
const attachmentInput = ref(null);
const selectedAttachments = ref([]);
const removeAttachmentIds = ref([]);
const replaceAttachmentId = ref(null);
const showEmojiPicker = ref(false);
const emojis = ['😀', '😂', '😍', '👍', '🎉', '❤️', '🙏', '🔥'];
const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const minutes = ['00', '15', '30', '45'];
const weekdays = [
  { value: 1, label: 'MONDAY' },
  { value: 2, label: 'TUESDAY' },
  { value: 3, label: 'WEDNESDAY' },
  { value: 4, label: 'THURSDAY' },
  { value: 5, label: 'FRIDAY' },
  { value: 6, label: 'SATURDAY' },
  { value: 0, label: 'SUNDAY' },
];
const localToday = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};
const timeSlots = ref([{ hour: '09', minute: '00' }]);
const form = ref({
  id: null,
  title: '',
  inbox_id: '',
  group_jid: '',
  group_jids: [],
  recipient_contact_ids: [],
  timezone: 'America/Sao_Paulo',
  delivery_channel: 'whatsapp',
  recipient_type: 'contact',
  recipient_contact_id: '',
  message: '',
  frequency: 'daily',
  starts_on: localToday(),
  ends_on: '',
  weekdays: [1],
  day_of_month: 1,
});
const apiBase = () => `/api/v1/accounts/${accountId.value}`;
const accessUrl = () => `${apiBase()}/most_scheduled_messages_access`;
const schedulesUrl = () => `${apiBase()}/most_scheduled_messages`;
const contactsUrl = () => `${schedulesUrl()}/contacts`;
const usableInboxes = computed(() =>
  inboxes.value.filter(inbox => enabledInboxIds.value.includes(inbox.id) && inbox.channel_type)
);
const channelInboxes = computed(() =>
  usableInboxes.value.filter(inbox => inbox.channel_type === form.value.delivery_channel)
);

const resetForm = () => {
  form.value = {
    id: null,
    title: '',
    inbox_id: usableInboxes.value.find(inbox => inbox.channel_type === 'whatsapp')?.id || '',
    group_jid: '',
    group_jids: [],
    recipient_contact_ids: [],
    timezone: defaultTimezone.value || 'America/Sao_Paulo',
    delivery_channel: 'whatsapp',
    recipient_type: 'contact',
    recipient_contact_id: '',
    message: '',
    frequency: 'daily',
    starts_on: localToday(),
    ends_on: '',
    weekdays: [1],
    day_of_month: 1,
  };
  timeSlots.value = [{ hour: '09', minute: '00' }];
  selectedAttachments.value = [];
  removeAttachmentIds.value = [];
  replaceAttachmentId.value = null;
  contacts.value = [];
  contactSearch.value = '';
  selectedContacts.value = [];
  groupJidsText.value = '';
};

const loadSettings = async () => {
  isLoading.value = true;
  try {
    const { data: access } = await axios.get(accessUrl());
    isSchedulerEnabled.value = Boolean(access.is_enabled);
    defaultTimezone.value = access.default_timezone || 'America/Sao_Paulo';
    inboxes.value = access.inboxes || [];
    enabledInboxIds.value = access.enabled_inbox_ids || [];
    if (!isSchedulerEnabled.value) {
      schedules.value = [];
      return;
    }
    const { data: items } = await axios.get(schedulesUrl());
    schedules.value = items;
    resetForm();
  } catch {
    useAlert(t('MOST_SCHEDULED_MESSAGES.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const onDeliveryChannelChange = () => {
  form.value.inbox_id = usableInboxes.value.find(
    inbox => inbox.channel_type === form.value.delivery_channel
  )?.id || '';
  form.value.recipient_type = 'contact';
  form.value.group_jid = '';
  form.value.recipient_contact_id = '';
  form.value.recipient_contact_ids = [];
  form.value.group_jids = [];
  groupJidsText.value = '';
  contacts.value = [];
  selectedContacts.value = [];
  contactSearch.value = '';
};

const setRecipientType = type => {
  form.value.recipient_type = type;
  form.value.group_jid = '';
  form.value.group_jids = [];
  form.value.recipient_contact_id = '';
  form.value.recipient_contact_ids = [];
  groupJidsText.value = '';
  contacts.value = [];
  selectedContacts.value = [];
  contactSearch.value = '';
};

const searchContacts = async () => {
  const query = contactSearch.value.trim();
  if (query.length < 2 || !form.value.inbox_id) {
    contacts.value = [];
    return;
  }
  try {
    const { data } = await axios.get(contactsUrl(), {
      params: {
        inbox_id: form.value.inbox_id,
        delivery_channel: form.value.delivery_channel,
        q: query,
      },
    });
    contacts.value = data.contacts;
  } catch {
    contacts.value = [];
    useAlert(t('MOST_SCHEDULED_MESSAGES.CONTACT_SEARCH_ERROR'));
  }
};

const onContactSearchInput = () => {
  queueContactSearch();
};

const onInboxChange = () => {
  contacts.value = [];
  selectedContacts.value = [];
  form.value.recipient_contact_id = '';
  form.value.recipient_contact_ids = [];
  contactSearch.value = '';
};

const queueContactSearch = () => {
  clearTimeout(contactSearchTimer);
  contactSearchTimer = setTimeout(searchContacts, 300);
};

const selectContact = contact => {
  const next = form.value.delivery_channel === 'email'
    ? [contact]
    : [...selectedContacts.value.filter(item => item.id !== contact.id), contact];
  selectedContacts.value = next;
  form.value.recipient_contact_ids = next.map(item => item.id);
  form.value.recipient_contact_id = next[0]?.id || '';
  contactSearch.value = '';
  contacts.value = [];
};

const removeSelectedContact = contactId => {
  selectedContacts.value = selectedContacts.value.filter(contact => contact.id !== contactId);
  form.value.recipient_contact_ids = selectedContacts.value.map(contact => contact.id);
  form.value.recipient_contact_id = form.value.recipient_contact_ids[0] || '';
};

const parsedGroupJids = () => groupJidsText.value
  .split(/[\n,;]+/)
  .map(value => value.trim())
  .filter(Boolean);

const saveSchedule = async () => {
  if (!form.value.title.trim()) {
    useAlert(t('MOST_SCHEDULED_MESSAGES.TITLE_REQUIRED'));
    return;
  }
  form.value.group_jids = form.value.recipient_type === 'group' ? parsedGroupJids() : [];
  form.value.group_jid = form.value.group_jids[0] || '';
  if (form.value.recipient_type === 'group' && !form.value.group_jids.length) {
    useAlert(t('MOST_SCHEDULED_MESSAGES.RECIPIENT_REQUIRED'));
    return;
  }
  if (form.value.recipient_type === 'contact' && !form.value.recipient_contact_ids.length) {
    useAlert(t('MOST_SCHEDULED_MESSAGES.CONTACT_REQUIRED'));
    return;
  }
  if (!form.value.message.trim()) {
    useAlert(t('MOST_SCHEDULED_MESSAGES.MESSAGE_REQUIRED'));
    return;
  }
  if (!form.value.inbox_id || !form.value.ends_on || timeSlots.value.length > 3) {
    useAlert(t('MOST_SCHEDULED_MESSAGES.REQUIRED_FIELDS'));
    return;
  }
  if (!channelInboxes.value.some(inbox => String(inbox.id) === String(form.value.inbox_id))) {
    useAlert(t('MOST_SCHEDULED_MESSAGES.INBOX_CHANNEL_REQUIRED'));
    return;
  }
  isSaving.value = true;
  const schedule = {
    ...form.value,
    schedule_times: timeSlots.value.map(slot => `${slot.hour}:${slot.minute}`),
  };
  const payload = new FormData();
  Object.entries(schedule).forEach(([key, value]) => {
    if (key === 'id' || key === 'attachment' || key === 'attachments' || value === null || value === undefined) return;
    if (Array.isArray(value)) value.forEach(item => payload.append(`most_scheduled_message[${key}][]`, item));
    else payload.append(`most_scheduled_message[${key}]`, value);
  });
  selectedAttachments.value.forEach(file => payload.append('most_scheduled_message[attachments][]', file));
  removeAttachmentIds.value.forEach(id => payload.append('most_scheduled_message[remove_attachment_ids][]', id));
  try {
    if (form.value.id) {
      await axios.patch(`${schedulesUrl()}/${form.value.id}`, payload);
    } else {
      await axios.post(schedulesUrl(), payload);
    }
    await loadSettings();
    useAlert(t('MOST_SCHEDULED_MESSAGES.SAVED'));
  } catch (error) {
    const errors = error?.response?.data?.errors;
    useAlert(errors?.join(' ') || t('MOST_SCHEDULED_MESSAGES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const editSchedule = schedule => {
  form.value = {
    id: schedule.id,
    title: schedule.title,
    inbox_id: schedule.inbox_id,
    group_jid: schedule.group_jid || '',
    group_jids: schedule.group_jids || (schedule.group_jid ? [schedule.group_jid] : []),
    recipient_contact_id: schedule.recipient_contact_id || schedule.recipient_contact?.id || '',
    recipient_contact_ids: schedule.recipient_contact_ids || schedule.recipient_contacts?.map(contact => contact.id) || [schedule.recipient_contact_id || schedule.recipient_contact?.id].filter(Boolean),
    timezone: schedule.timezone || defaultTimezone.value || 'America/Sao_Paulo',
    delivery_channel: schedule.delivery_channel || 'whatsapp',
    recipient_type: schedule.recipient_type || 'group',
    attachments: schedule.attachments || (schedule.attachment ? [schedule.attachment] : []),
    message: schedule.message,
    frequency: schedule.frequency,
    starts_on: schedule.starts_on,
    ends_on: schedule.ends_on,
    weekdays: schedule.weekdays || [],
    day_of_month: schedule.day_of_month || 1,
  };
  selectedContacts.value = schedule.recipient_contacts?.length ? schedule.recipient_contacts : (schedule.recipient_contact ? [schedule.recipient_contact] : []);
  groupJidsText.value = (schedule.group_jids || (schedule.group_jid ? [schedule.group_jid] : [])).join('\n');
  contactSearch.value = '';
  contacts.value = [];
  selectedAttachments.value = [];
  removeAttachmentIds.value = [];
  replaceAttachmentId.value = null;
  timeSlots.value = schedule.schedule_times.map(slot => {
    const [hour, minute] = slot.split(':');
    return { hour, minute };
  });
};

const toggleSchedule = async schedule => {
  try {
    await axios.patch(`${schedulesUrl()}/${schedule.id}`, {
      most_scheduled_message: { enabled: !schedule.enabled },
    });
    await loadSettings();
  } catch {
    useAlert(t('MOST_SCHEDULED_MESSAGES.SAVE_ERROR'));
  }
};

const deleteSchedule = async schedule => {
  if (!window.confirm(t('MOST_SCHEDULED_MESSAGES.CONFIRM_DELETE'))) return;
  try {
    await axios.delete(`${schedulesUrl()}/${schedule.id}`);
    await loadSettings();
  } catch {
    useAlert(t('MOST_SCHEDULED_MESSAGES.DELETE_ERROR'));
  }
};

const wrapMessageSelection = marker => {
  const input = messageTextarea.value;
  if (!input) return;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const selected = form.value.message.slice(start, end) || 'texto';
  const replacement = selected.split('\n').map(line => line.trim() ? line.replace(/^(\s*)(.*?)(\s*)$/, (_, before, value, after) => `${before}${marker}${value}${marker}${after}`) : line).join('\n');
  form.value.message = `${form.value.message.slice(0, start)}${replacement}${form.value.message.slice(end)}`;
  nextTick(() => {
    input.focus();
    input.setSelectionRange(start + marker.length, start + marker.length + selected.length);
  });
};

const messagePreview = computed(() => previewSegments(form.value.message));
const toggleBullets = () => {
  const input = messageTextarea.value;
  if (!input) return;
  const result = bulletSelection(form.value.message, input.selectionStart, input.selectionEnd);
  form.value.message = result.text;
  nextTick(() => {
    input.focus();
    input.setSelectionRange(result.start, result.end);
  });
};

const insertEmoji = emoji => {
  const input = messageTextarea.value;
  const position = input?.selectionStart ?? form.value.message.length;
  form.value.message = `${form.value.message.slice(0, position)}${emoji}${form.value.message.slice(position)}`;
  showEmojiPicker.value = false;
  nextTick(() => {
    input?.focus();
    input?.setSelectionRange(position + emoji.length, position + emoji.length);
  });
};

const handleAttachmentSelection = event => {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  const replacingId = replaceAttachmentId.value;
  const retainedExisting = (form.value.attachments || []).filter(
    file => !removeAttachmentIds.value.includes(file.id) && file.id !== replacingId
  ).length;
  if (retainedExisting + selectedAttachments.value.length + files.length > 3) {
    useAlert(t('MOST_SCHEDULED_MESSAGES.ATTACHMENT_LIMIT'));
    event.target.value = '';
    return;
  }
  if (files.some(file => file.size > 16 * 1024 * 1024)) {
    useAlert(t('MOST_SCHEDULED_MESSAGES.ATTACHMENT_TOO_LARGE'));
    event.target.value = '';
    return;
  }
  if (replacingId) {
    removeAttachmentIds.value = [...removeAttachmentIds.value, replacingId];
    replaceAttachmentId.value = null;
  }
  selectedAttachments.value = [...selectedAttachments.value, ...files];
  if (attachmentInput.value) attachmentInput.value.value = '';
};

const removeSelectedAttachment = index => {
  selectedAttachments.value.splice(index, 1);
};

const removeExistingAttachment = id => {
  removeAttachmentIds.value = [...removeAttachmentIds.value, id];
};

const replaceExistingAttachment = id => {
  replaceAttachmentId.value = id;
  attachmentInput.value?.click();
};

const addTime = () => {
  if (timeSlots.value.length < 3) timeSlots.value.push({ hour: '09', minute: '00' });
};

const removeTime = index => {
  if (timeSlots.value.length > 1) timeSlots.value.splice(index, 1);
};

const formatDateTime = value =>
  value ? new Date(value).toLocaleString() : '—';

onMounted(loadSettings);
</script>

<template>
  <div class="flex flex-col w-full max-w-7xl ltr:mr-auto rtl:ml-auto gap-6">
    <BaseSettingsHeader
      :title="t('MOST_SCHEDULED_MESSAGES.TITLE')"
      :description="t('MOST_SCHEDULED_MESSAGES.DESCRIPTION')"
    />
    <div v-if="isLoading" class="py-6 text-sm text-n-slate-11">
      {{ t('MOST_SCHEDULED_MESSAGES.LOADING') }}
    </div>
    <template v-else>
      <div v-if="!isSchedulerEnabled" class="rounded-lg border border-n-weak p-4 text-sm text-n-slate-11">
        Message Scheduler is not enabled for this installation. Contact your Super Admin to enable it and authorize this inbox.
      </div>
      <template v-else>
      <section v-if="usableInboxes.length" class="flex flex-col gap-4">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('MOST_SCHEDULED_MESSAGES.NEW') }}
        </h2>
        <p v-if="!channelInboxes.length" class="text-sm text-n-slate-11">{{ t('MOST_SCHEDULED_MESSAGES.NO_CHANNEL_INBOX') }}</p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.TITLE_LABEL') }}
            <input v-model="form.title" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" required />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.DELIVERY_CHANNEL') }}
            <select v-model="form.delivery_channel" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" @change="onDeliveryChannelChange">
              <option value="whatsapp">WhatsApp</option>
              <option value="email">E-mail</option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.INBOX') }}
            <select v-model="form.inbox_id" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" @change="onInboxChange">
              <option v-for="inbox in channelInboxes" :key="inbox.id" :value="inbox.id">
                {{ inbox.name }}
              </option>
            </select>
          </label>
          <fieldset v-if="form.delivery_channel === 'whatsapp'" class="md:col-span-2 flex flex-col gap-2">
            <legend class="text-sm font-medium text-n-slate-12">
              {{ t('MOST_SCHEDULED_MESSAGES.WHATSAPP_RECIPIENT_TYPE') }}
            </legend>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 cursor-pointer">
                <input :checked="form.recipient_type === 'contact'" type="radio" class="mt-1" @change="setRecipientType('contact')" />
                <span class="flex flex-col gap-1">
                  <strong class="text-sm text-n-slate-12">{{ t('MOST_SCHEDULED_MESSAGES.WHATSAPP_CONTACT') }}</strong>
                  <span class="text-xs text-n-slate-11">{{ t('MOST_SCHEDULED_MESSAGES.CONTACT_SEARCH_HELP') }}</span>
                </span>
              </label>
              <label class="flex items-start gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 cursor-pointer">
                <input :checked="form.recipient_type === 'group'" type="radio" class="mt-1" @change="setRecipientType('group')" />
                <span class="flex flex-col gap-1">
                  <strong class="text-sm text-n-slate-12">{{ t('MOST_SCHEDULED_MESSAGES.WHATSAPP_GROUP') }}</strong>
                  <span class="text-xs text-n-slate-11">{{ t('MOST_SCHEDULED_MESSAGES.GROUP_HELP') }}</span>
                </span>
              </label>
            </div>
          </fieldset>
          <label v-if="form.delivery_channel === 'email'" class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.EMAIL_RECIPIENT_TYPE') }}
            <input :value="t('MOST_SCHEDULED_MESSAGES.EMAIL_CONTACT_ONLY')" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" disabled />
          </label>
          <label v-if="form.recipient_type === 'group' && form.delivery_channel === 'whatsapp'" class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.GROUP') }}
            <textarea v-model="groupJidsText" rows="3" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" :placeholder="t('MOST_SCHEDULED_MESSAGES.GROUP_PLACEHOLDER')" required />
            <span class="text-xs">Informe um código de grupo por linha ou separe os códigos por vírgula. {{ t('MOST_SCHEDULED_MESSAGES.GROUP_HELP') }}</span>
          </label>
          <div v-if="form.recipient_type === 'contact'" class="relative flex flex-col gap-1 text-sm text-n-slate-11">
            <label for="scheduled-message-contact">{{ form.delivery_channel === 'email' ? t('MOST_SCHEDULED_MESSAGES.EMAIL_CONTACT') : t('MOST_SCHEDULED_MESSAGES.WHATSAPP_CONTACT') }}</label>
            <input id="scheduled-message-contact" v-model="contactSearch" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" :placeholder="t('MOST_SCHEDULED_MESSAGES.CONTACT_SEARCH')" autocomplete="off" @input="onContactSearchInput" />
            <ul v-if="contacts.length" class="absolute z-20 top-full mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 shadow-lg max-h-56 overflow-y-auto">
              <li v-for="contact in contacts" :key="contact.id">
                <button type="button" class="w-full px-3 py-2 text-left hover:bg-n-slate-2" @click="selectContact(contact)">
                  <span class="block text-n-slate-12">{{ contact.name || contact.email || contact.phone_number }}</span>
                  <span class="block text-xs text-n-slate-11">{{ form.delivery_channel === 'email' ? contact.email : contact.phone_number }}</span>
                </button>
              </li>
            </ul>
            <div v-if="selectedContacts.length" class="flex flex-wrap gap-2">
              <span v-for="contact in selectedContacts" :key="contact.id" class="inline-flex items-center gap-2 rounded-full bg-n-slate-2 px-3 py-1 text-xs text-n-slate-12">
                {{ contact.name || contact.email || contact.phone_number }} · {{ form.delivery_channel === 'email' ? contact.email : contact.phone_number }}
                <button type="button" class="font-bold" :aria-label="t('MOST_SCHEDULED_MESSAGES.REMOVE_CONTACT')" @click="removeSelectedContact(contact.id)">×</button>
              </span>
            </div>
            <p class="text-xs text-n-slate-11">{{ t('MOST_SCHEDULED_MESSAGES.CONTACT_SEARCH_HELP') }}</p>
          </div>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.FREQUENCY') }}
            <select v-model="form.frequency" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12">
              <option value="once">{{ t('MOST_SCHEDULED_MESSAGES.ONCE') }}</option>
              <option value="daily">{{ t('MOST_SCHEDULED_MESSAGES.DAILY') }}</option>
              <option value="weekly">{{ t('MOST_SCHEDULED_MESSAGES.WEEKLY') }}</option>
              <option value="monthly">{{ t('MOST_SCHEDULED_MESSAGES.MONTHLY') }}</option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.START_DATE') }}
            <input v-model="form.starts_on" type="date" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" required />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.END_DATE') }}
            <input v-model="form.ends_on" type="date" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" required />
          </label>
        </div>
        <fieldset v-if="form.frequency === 'weekly'" class="flex flex-wrap gap-3">
          <legend class="mb-2 text-sm font-medium">{{ t('MOST_SCHEDULED_MESSAGES.WEEKDAYS') }}</legend>
          <label v-for="day in weekdays" :key="day.value" class="flex items-center gap-2">
            <input v-model="form.weekdays" type="checkbox" :value="day.value" />
            <span class="text-sm">{{ t(`MOST_SCHEDULED_MESSAGES.${day.label}`) }}</span>
          </label>
        </fieldset>
        <label v-if="form.frequency === 'monthly'" class="flex flex-col gap-1 text-sm text-n-slate-11 max-w-xs">
          {{ t('MOST_SCHEDULED_MESSAGES.MONTH_DAY') }}
          <input v-model.number="form.day_of_month" type="number" min="1" max="31" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" />
        </label>
        <div class="flex flex-col gap-2">
          <p class="text-sm font-medium text-n-slate-12">{{ t('MOST_SCHEDULED_MESSAGES.SEND_TIMES') }}</p>
          <p class="text-xs text-n-slate-11">{{ t('MOST_SCHEDULED_MESSAGES.TIME_HELP') }}</p>
          <div v-for="(slot, index) in timeSlots" :key="index" class="flex items-center gap-2">
            <select v-model="slot.hour" class="w-28 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12">
              <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
            </select>
            <select v-model="slot.minute" class="w-28 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12">
              <option v-for="minute in minutes" :key="minute" :value="minute">{{ minute }}</option>
            </select>
            <Button v-if="timeSlots.length > 1" icon="i-lucide-trash-2" variant="ghost" @click="removeTime(index)" />
          </div>
          <Button
            v-if="timeSlots.length < 3"
            :label="t('MOST_SCHEDULED_MESSAGES.ADD_TIME')"
            variant="secondary"
            @click="addTime"
          />
        </div>
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        <div class="flex flex-col gap-2 min-w-0">
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.MESSAGE') }}
            <textarea ref="messageTextarea" v-model="form.message" rows="12" class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-n-slate-12" required />
          </label>
          <div class="flex flex-wrap items-center gap-2">
            <button type="button" class="rounded border border-n-weak px-3 py-1 text-sm font-bold" :aria-label="t('MOST_SCHEDULED_MESSAGES.BOLD')" @click="wrapMessageSelection('*')">B</button>
            <button type="button" class="rounded border border-n-weak px-3 py-1 text-sm italic" :aria-label="t('MOST_SCHEDULED_MESSAGES.ITALIC')" @click="wrapMessageSelection('_')">I</button>
            <button type="button" class="rounded border border-n-weak px-3 py-1 text-sm" :aria-label="t('MOST_SCHEDULED_MESSAGES.BULLETS')" @click="toggleBullets">• {{ t('MOST_SCHEDULED_MESSAGES.BULLETS') }}</button>
            <div class="relative">
              <button type="button" class="rounded border border-n-weak px-3 py-1 text-sm" @click="showEmojiPicker = !showEmojiPicker">😊 {{ t('MOST_SCHEDULED_MESSAGES.EMOJI') }}</button>
              <div v-if="showEmojiPicker" class="absolute z-10 mt-1 flex gap-1 rounded-lg border border-n-weak bg-white p-2 shadow-lg">
                <button v-for="emoji in emojis" :key="emoji" type="button" class="text-xl" @click="insertEmoji(emoji)">{{ emoji }}</button>
              </div>
            </div>
            <label class="rounded border border-n-weak px-3 py-1 text-sm cursor-pointer">
              {{ t('MOST_SCHEDULED_MESSAGES.ATTACH_FILE') }}
              <input ref="attachmentInput" type="file" class="hidden" multiple accept="image/*,video/mp4,.pdf,.doc,.docx,.xls,.xlsx,.txt" @change="handleAttachmentSelection" />
            </label>
          </div>
          <p class="text-xs text-n-slate-11">{{ t('MOST_SCHEDULED_MESSAGES.FORMAT_HELP') }}</p>
          <p class="text-xs text-n-slate-11">{{ t('MOST_SCHEDULED_MESSAGES.ATTACHMENT_HELP') }}</p>
          <div v-for="file in (form.attachments || []).filter(item => !removeAttachmentIds.includes(item.id))" :key="`existing-${file.id}`" class="flex items-center gap-2 text-sm">
            <a :href="file.url" target="_blank" rel="noopener noreferrer" class="text-n-brand">{{ file.filename }}</a>
            <button type="button" class="text-n-brand" @click="replaceExistingAttachment(file.id)">{{ t('MOST_SCHEDULED_MESSAGES.REPLACE_ATTACHMENT') }}</button>
            <button type="button" class="text-rose-600" @click="removeExistingAttachment(file.id)">{{ t('MOST_SCHEDULED_MESSAGES.REMOVE_ATTACHMENT') }}</button>
          </div>
          <div v-for="(file, index) in selectedAttachments" :key="`selected-${index}`" class="flex items-center gap-2 text-sm">
            <span>{{ file.name }}</span>
            <button type="button" class="text-rose-600" @click="removeSelectedAttachment(index)">{{ t('MOST_SCHEDULED_MESSAGES.REMOVE_ATTACHMENT') }}</button>
          </div>
        </div>
        <aside class="min-w-0 rounded-xl border border-n-weak bg-n-alpha-2 p-4" aria-live="polite" aria-atomic="true">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">{{ t('MOST_SCHEDULED_MESSAGES.PREVIEW_TITLE') }}</h3>
          <div class="rounded-lg bg-n-solid-1 p-4 shadow-sm text-n-slate-12">
            <p v-if="!form.message" class="text-sm text-n-slate-10">{{ t('MOST_SCHEDULED_MESSAGES.PREVIEW_EMPTY') }}</p>
            <p v-else class="whitespace-pre-wrap break-words text-sm leading-relaxed"><span v-for="(segment, index) in messagePreview" :key="index" :class="{ 'font-bold': segment.bold, italic: segment.italic }">{{ segment.text }}</span></p>
          </div>
          <p class="text-xs text-n-slate-11 mt-3">{{ t('MOST_SCHEDULED_MESSAGES.PREVIEW_HELP') }}</p>
        </aside>
        </div>
        <Button
          :label="t(form.id ? 'MOST_SCHEDULED_MESSAGES.UPDATE' : 'MOST_SCHEDULED_MESSAGES.CREATE')"
          :is-loading="isSaving"
          @click="saveSchedule"
        />
      </section>

      <section class="flex flex-col gap-3">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('MOST_SCHEDULED_MESSAGES.SCHEDULES') }}
        </h2>
        <p v-if="!schedules.length" class="text-sm text-n-slate-11">
          {{ t('MOST_SCHEDULED_MESSAGES.EMPTY') }}
        </p>
        <article
          v-for="schedule in schedules"
          :key="schedule.id"
          class="flex flex-col gap-3 p-4 border border-n-weak rounded-lg"
        >
          <div class="flex flex-wrap justify-between gap-2">
            <strong class="text-sm text-n-slate-12">{{ schedule.title }}</strong>
            <span class="text-xs text-n-slate-11">
              {{ schedule.enabled ? t('MOST_SCHEDULED_MESSAGES.ENABLED') : t('MOST_SCHEDULED_MESSAGES.PAUSED') }}
            </span>
          </div>
          <p class="text-sm text-n-slate-11">
            {{ schedule.delivery_channel === 'email' ? 'E-mail' : 'WhatsApp' }} · {{ schedule.recipient }} · {{ schedule.message }}<span v-if="schedule.attachments?.length || schedule.attachment"> · {{ (schedule.attachments || [schedule.attachment]).map(file => file.filename).join(', ') }}</span>
          </p>
          <p class="text-xs text-n-slate-11">
            {{ t('MOST_SCHEDULED_MESSAGES.NEXT_RUN') }}: {{ formatDateTime(schedule.next_run_at) }}
          </p>
          <div class="flex gap-2">
            <Button
              :label="t('MOST_SCHEDULED_MESSAGES.EDIT')"
              variant="secondary"
              @click="editSchedule(schedule)"
            />
            <Button
              :label="schedule.enabled ? t('MOST_SCHEDULED_MESSAGES.PAUSE') : t('MOST_SCHEDULED_MESSAGES.RESUME')"
              variant="secondary"
              @click="toggleSchedule(schedule)"
            />
            <Button
              :label="t('MOST_SCHEDULED_MESSAGES.DELETE')"
              variant="secondary"
              @click="deleteSchedule(schedule)"
            />
          </div>
        </article>
      </section>
      </template>
    </template>
  </div>
</template>
